<template>
  <div class="restaurant-tables-page">
    <div class="page-header">
      <div class="page-header__title">
        <h1>مدیریت میزها</h1>
        <p>مدیریت میزها و سالن‌های رستوران</p>
      </div>
      <ab-ui-button variant="primary" @click="goToCashier">
        <span>{{ $t("newInvoice") || "فاکتور جدید" }}</span>
        <i class="mdi mdi-receipt-text-plus" aria-hidden="true" />
      </ab-ui-button>
    </div>

    <!-- summary cards -->
    <div class="summary-cards">
      <div class="summary-card summary-card--inactive">
        <span class="summary-card__value">{{ counts.inactive }}</span>
        <span class="summary-card__label">غیرفعال</span>
      </div>
      <div class="summary-card summary-card--reserved">
        <span class="summary-card__value">{{ counts.reserved }}</span>
        <span class="summary-card__label">رزرو شده</span>
      </div>
      <div class="summary-card summary-card--busy">
        <span class="summary-card__value">{{ counts.busy }}</span>
        <span class="summary-card__label">میزهای پر</span>
      </div>
      <div class="summary-card summary-card--free">
        <span class="summary-card__value">{{ counts.free }}</span>
        <span class="summary-card__label">میزهای آزاد</span>
      </div>
      <div class="summary-card summary-card--total">
        <span class="summary-card__value">{{ counts.total }}</span>
        <span class="summary-card__label">کل میزها</span>
      </div>
    </div>

    <!-- view toggle + hall filter -->
    <div class="toolbar">
      <div class="view-toggle">
        <button
          class="view-toggle__btn"
          :class="{ 'view-toggle__btn--active': viewMode === 'card' }"
          @click="viewMode = 'card'"
        >
          <i class="mdi mdi-view-grid-outline" aria-hidden="true" />
          <span>نمایش کارتی</span>
        </button>
        <button
          class="view-toggle__btn"
          :class="{ 'view-toggle__btn--active': viewMode === 'visual' }"
          @click="viewMode = 'visual'"
        >
          <i class="mdi mdi-format-list-group" aria-hidden="true" />
          <span>نمایش بصری</span>
        </button>
      </div>
      <div class="hall-filter">
        <button
          class="hall-filter__btn"
          :class="{ 'hall-filter__btn--active': activeHallId === null }"
          :style="activeHallId === null ? activeHallBtnStyle : {}"
          @click="selectHall(null)"
        >
          همه سالن‌ها
        </button>
        <button
          v-for="hall in halls"
          :key="hall.id"
          class="hall-filter__btn"
          :class="{ 'hall-filter__btn--active': activeHallId === hall.id }"
          :style="activeHallId === hall.id ? activeHallBtnStyle : {}"
          @click="selectHall(hall.id)"
        >
          سالن {{ hall.name }}
        </button>
      </div>
    </div>

    <!-- legend -->
    <div class="legend">
      <span class="legend-item legend-item--free">آزاد</span>
      <span class="legend-item legend-item--busy">پر</span>
      <span class="legend-item legend-item--reserved">رزرو</span>
      <span class="legend-item legend-item--inactive">غیرفعال</span>
    </div>

    <!-- content: visual or card -->
    <ab-ui-card class="content-card">
      <div v-if="loading" class="loading-wrap">
        <span>در حال بارگذاری از سرور...</span>
      </div>
      <div v-else class="content-card__body">
        <template v-if="viewMode === 'visual'">
          <div class="canvas-header">
            <span>{{ activeHall ? "سالن " + activeHall.name : "همه سالن‌ها" }}</span>
          </div>
          <div class="canvas" ref="canvas">
            <div
              v-for="table in tablesFiltered"
              :key="table.id"
              class="table-visual-wrap table-visual-wrap--clickable"
              :style="{ left: table.x + 'px', top: table.y + 'px' }"
              @click="openTableDialog(table)"
            >
              <div
                class="table-visual"
                :class="[
                  'table-visual--' + table.shape,
                  'table-visual--status-' + table.status,
                  { 'table-visual--disabled': !table.isActive },
                ]"
              >
                <span class="table-visual__number">{{ table.number }}</span>
                <span class="table-visual__seats">{{ table.capacity }} نفر</span>
                <span v-if="table.guestsCount > 0" class="table-visual__guests"
                  >{{ table.guestsCount }} نفر</span
                >
              </div>
            </div>
          </div>
        </template>

        <!-- card: grid of table cards - تغییرات مستقیم روی کارت -->
        <template v-else>
          <div class="table-cards">
            <div
              v-for="table in tablesFiltered"
              :key="table.id"
              class="table-card"
              :class="'table-card--' + table.status"
            >
              <div class="table-card__dot" />
              <div class="table-card__head">
                <span class="table-card__title">میز {{ table.number }}</span>
                <span class="table-card__hall">سالن {{ table.hallName }}</span>
              </div>
              <div class="table-card__capacity">
                <i class="mdi mdi-account-group-outline" aria-hidden="true" />
                <span>{{ table.capacity }} نفره</span>
              </div>
              <div class="table-card__status">
                <select
                  :value="table.status"
                  class="table-card__status-select"
                  @change="onCardStatusChange(table, $event.target.value)"
                >
                  <option
                    v-for="opt in statusOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.text }}
                  </option>
                </select>
              </div>
              <div class="table-card__active-row">
                <span class="table-card__active-label">فعال/غیرفعال</span>
                <button
                  type="button"
                  class="table-card__active-toggle"
                  :class="{ 'table-card__active-toggle--on': table.isActive }"
                  @click.stop="onCardActiveChange(table)"
                >
                  <span class="table-card__active-dot" />
                </button>
              </div>
              <template v-if="table.status === 'busy' && table.orderNumber">
                <div class="table-card__row">
                  <span class="table-card__label">سفارش</span>
                  <span class="table-card__value">#{{ table.orderNumber }}</span>
                </div>
                <div class="table-card__row">
                  <span class="table-card__label">مبلغ</span>
                  <span class="table-card__value">{{
                    formatAmount(table.orderAmount)
                  }}</span>
                </div>
                <div class="table-card__row">
                  <i class="mdi mdi-clock-outline" aria-hidden="true" />
                  <span class="table-card__label">مدت زمان</span>
                  <span class="table-card__value"
                    >{{ table.occupiedDurationMinutes }} دقیقه</span
                  >
                </div>
                <div class="table-card__row">
                  <i class="mdi mdi-account-multiple-outline" aria-hidden="true" />
                  <span class="table-card__label">تعداد مهمان</span>
                  <span class="table-card__value">{{ table.guestsCount }} نفر</span>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </ab-ui-card>

    <!-- دیالوگ اطلاعات و تنظیمات میز -->
    <v-dialog
      v-model="tableDialogVisible"
      max-width="420"
      persistent
      content-class="table-dialog"
      transition="scale-transition"
      @click:outside="closeTableDialog"
    >
      <div v-if="editingTable" class="table-dialog__inner">
        <div class="table-dialog__title">میز {{ editingTable.number }}</div>
        <div class="table-dialog__info">
          <div class="table-dialog__row">
            <span class="table-dialog__label">سالن</span>
            <span class="table-dialog__value">سالن {{ editingTable.hallName }}</span>
          </div>
          <div class="table-dialog__row">
            <span class="table-dialog__label">ظرفیت</span>
            <span class="table-dialog__value">{{ editingTable.capacity }} نفر</span>
          </div>
        </div>
        <div class="table-dialog__form">
          <div class="table-dialog__field">
            <label class="table-dialog__label">وضعیت</label>
            <select v-model="dialogForm.status" class="table-dialog__select">
              <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                {{ opt.text }}
              </option>
            </select>
          </div>
          <div class="table-dialog__field table-dialog__field--row">
            <label class="table-dialog__label">فعال / غیرفعال</label>
            <button
              type="button"
              class="table-dialog__toggle"
              :class="{ 'table-dialog__toggle--on': dialogForm.isActive }"
              @click="dialogForm.isActive = !dialogForm.isActive"
            >
              <span class="table-dialog__toggle-dot" />
            </button>
          </div>
        </div>
        <div class="table-dialog__actions">
          <button
            type="button"
            class="table-dialog__btn table-dialog__btn--secondary"
            @click="closeTableDialog"
          >
            انصراف
          </button>
          <button
            type="button"
            class="table-dialog__btn table-dialog__btn--primary"
            @click="saveTableDialog"
          >
            ذخیره
          </button>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" bottom right :timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import AbUiButton from "./partial/AbUiButton.vue";
import AbUiCard from "./partial/AbUiCard.vue";
import { getLastBranchId, setLastBranchId } from './partial/branchStorage.js'
import toastMixin from '@/Mixins/toastMixin'

export default {
  name: "RestaurantTablesPage",
  mixins: [toastMixin],
  components: {
    AbUiButton,
    AbUiCard,
  },
  data() {
    return {
      viewMode: "visual", // 'visual' | 'card'
      activeHallId: null, // null = all halls
      halls: [],
      tables: [],
      loading: false,
      currentBranchId: null,
      statusOptions: [
        { value: "free", text: "آزاد" },
        { value: "busy", text: "پر" },
        { value: "reserved", text: "رزرو" },
        { value: "inactive", text: "غیرفعال" },
      ],
      tableDialogVisible: false,
      editingTable: null,
      dialogForm: { status: "free", isActive: true },
    };
  },
  computed: {
    /** همان آبی دکمه فاکتور جدید (primary) */
    activeHallBtnStyle() {
      return {
        backgroundColor: "#1976d2",
        borderColor: "#1976d2",
        color: "#fff",
      };
    },
    activeHall() {
      if (this.activeHallId == null) return null;
      return this.halls.find((h) => h.id === this.activeHallId) || null;
    },
    tablesFiltered() {
      if (this.activeHallId == null) return this.tables;
      return this.tables.filter((t) => t.hallId === this.activeHallId);
    },
    counts() {
      const list =
        this.activeHallId == null
          ? this.tables
          : this.tables.filter((t) => t.hallId === this.activeHallId);
      return {
        inactive: list.filter((t) => t.status === "inactive").length,
        reserved: list.filter((t) => t.status === "reserved").length,
        busy: list.filter((t) => t.status === "busy").length,
        free: list.filter((t) => t.status === "free").length,
        total: list.length,
      };
    },
  },
  async created() {
    await this.ensureBranchAndLoad();
  },
  methods: {
    async ensureBranchAndLoad() {
      let branchId = getLastBranchId();
      if ((!branchId || branchId === "") && this.$axios && this.$endpoint) {
        try {
          const res = await this.$axios.get(this.$endpoint + "branch/list", {
            params: { type: "all", options: { itemsPerPage: 1000 }, page: 1 },
          });
          const raw = this.normalizeBranchListResponse(res.data);
          if (raw.length) {
            branchId = raw[0].id ?? raw[0].branch_id;
            if (branchId != null) setLastBranchId(branchId);
          }
        } catch (e) {}
      }
      await this.loadHalls(branchId);
    },
    async loadHalls(branchId) {
      if (!this.$axios || !this.$endpoint) {
        this.halls = [];
        this.activeHallId = null;
        this.tables = [];
        return;
      }
      this.loading = true;
      try {
        const normalizedBranchId =
          branchId != null && branchId !== "" ? Number(branchId) : null;
        const params = {
          options: { page: 1, itemsPerPage: 1000 },
          search: null,
        };
        if (normalizedBranchId != null) {
          params.branch_id = normalizedBranchId;
        }
        const hallsRes = await this.$axios.get(this.$endpoint + "restaurant/halls", {
          params,
        });
        const hallsRaw = this.normalizeHallsResponse(hallsRes.data);
        let halls = hallsRaw;
        if (normalizedBranchId != null) {
          halls = halls.filter((h) => {
            const bid = h.branch_id ?? h.branchId;
            if (bid == null) return false;
            return (
              Number(bid) === normalizedBranchId ||
              String(bid) === String(normalizedBranchId)
            );
          });
        }
        this.halls = halls.map((h) => ({
          id: h.id,
          branchId: h.branch_id ?? h.branchId ?? null,
          name: String(h.name ?? h.hall_name ?? h.title ?? ""),
        }));
        this.currentBranchId = normalizedBranchId;
        let firstHallId = this.halls.length ? this.halls[0].id : null;
        this.activeHallId = firstHallId;
        await this.fetchTables(firstHallId);
        // اگر از API سالنی نیامد، از لیست میزها سالن‌ها را استخراج کن
        if (this.halls.length === 0 && this.tables.length > 0) {
          const hallIds = [
            ...new Set(this.tables.map((t) => t.hallId).filter((id) => id != null)),
          ];
          this.halls = hallIds.map((id) => ({
            id,
            branchId: normalizedBranchId,
            name: "سالن " + id,
          }));
          if (this.halls.length) {
            this.activeHallId = this.halls[0].id;
          }
        }
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.halls = [];
        this.tables = [];
        const normalizedBranchId =
          branchId != null && branchId !== "" ? Number(branchId) : null;
        this.currentBranchId = normalizedBranchId;
        try {
          await this.fetchTables(null);
          if (this.halls.length === 0 && this.tables.length > 0) {
            const hallIds = [
              ...new Set(this.tables.map((t) => t.hallId).filter((id) => id != null)),
            ];
            this.halls = hallIds.map((id) => ({
              id,
              branchId: normalizedBranchId,
              name: "سالن " + id,
            }));
            if (this.halls.length) this.activeHallId = this.halls[0].id;
          }
        } catch (e) {}
      } finally {
        this.loading = false;
      }
    },
    async selectHall(hallId) {
      this.activeHallId = hallId;
      await this.fetchTables(hallId);
    },
    async fetchTables(hallId) {
      if (!this.$axios || !this.$endpoint) {
        this.tables = [];
        return;
      }
      this.loading = true;
      try {
        const branchId =
          this.currentBranchId != null ? this.currentBranchId : getLastBranchId();
        const normalizedBranchId =
          branchId != null && branchId !== "" ? Number(branchId) : null;
        const params = {
          options: { page: 1, itemsPerPage: 1000 },
          search: null,
        };
        if (normalizedBranchId != null) {
          params.branch_id = normalizedBranchId;
        }
        if (hallId != null) {
          params.hall_id = Number(hallId);
        }
        const tablesRes = await this.$axios.get(this.$endpoint + "restaurant/tables", {
          params,
        });
        const tablesRaw = Array.isArray(tablesRes.data?.data)
          ? tablesRes.data.data
          : Array.isArray(tablesRes.data?.tables)
          ? tablesRes.data.tables
          : Array.isArray(tablesRes.data)
          ? tablesRes.data
          : [];
        const tables = tablesRaw.map((t) => ({
          id: t.id,
          hallId: t.hall_id ?? t.hallId,
          number: String(t.number ?? ""),
          capacity: t.capacity ?? 0,
          shape: t.shape || "rectangle",
          status: t.status || "free",
          isActive:
            t.is_active !== undefined
              ? !!t.is_active
              : t.isActive !== undefined
              ? !!t.isActive
              : true,
          x: Number(t.pos_x ?? t.x ?? 0),
          y: Number(t.pos_y ?? t.y ?? 0),
          guestsCount: t.guests_count ?? t.guestsCount ?? 0,
          orderNumber: t.order_number ?? t.orderNumber ?? null,
          orderAmount: t.order_amount ?? t.orderAmount ?? null,
          occupiedDurationMinutes:
            t.occupied_duration_minutes ?? t.occupiedDurationMinutes ?? null,
        }));
        this.tables = this.enrichTables(tables, this.halls);
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.tables = [];
      } finally {
        this.loading = false;
      }
    },
    normalizeBranchListResponse(data) {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (Array.isArray(data.data)) return data.data;
      if (data.data && Array.isArray(data.data.items)) return data.data.items;
      if (data.data && Array.isArray(data.data.data)) return data.data.data;
      if (Array.isArray(data.items)) return data.items;
      return [];
    },
    normalizeHallsResponse(data) {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (Array.isArray(data.data)) return data.data;
      if (Array.isArray(data.halls)) return data.halls;
      if (Array.isArray(data.items)) return data.items;
      if (data.data && Array.isArray(data.data.items)) return data.data.items;
      if (data.data && Array.isArray(data.data.data)) return data.data.data;
      if (data.result && Array.isArray(data.result)) return data.result;
      if (data.result && data.result.data && Array.isArray(data.result.data))
        return data.result.data;
      return [];
    },
    enrichTables(tables, halls) {
      const hallMap = (halls || []).reduce((acc, h) => ({ ...acc, [h.id]: h.name }), {});
      return tables.map((t) => ({
        ...t,
        hallName: t.hall_name ?? t.hallName ?? hallMap[t.hallId] ?? "",
      }));
    },
    statusLabel(status) {
      const map = { free: "آزاد", busy: "پر", reserved: "رزرو", inactive: "غیرفعال" };
      return map[status] || status;
    },
    formatAmount(v) {
      if (v == null) return "—";
      return new Intl.NumberFormat("fa-IR").format(v) + " ریال";
    },
    goToCashier() {
      this.$router.push("/sell/indexCasher");
    },
    openTableDialog(table) {
      this.editingTable = table;
      this.dialogForm = { status: table.status, isActive: table.isActive };
      this.tableDialogVisible = true;
    },
    closeTableDialog() {
      this.tableDialogVisible = false;
      this.editingTable = null;
    },
    async saveTableDialog() {
      if (!this.editingTable || !this.$axios || !this.$endpoint) {
        this.closeTableDialog();
        return;
      }
      const t = this.editingTable;
      const payload = this.buildBulkUpdatePayload(t, {
        status: this.dialogForm.status,
        isActive: this.dialogForm.isActive,
      });
      try {
        await this.$axios.put(this.$endpoint + "restaurant/tables/bulk-update", payload);
        const idx = this.tables.findIndex((tbl) => tbl.id === t.id);
        if (idx !== -1) {
          this.tables[idx].status = this.dialogForm.status;
          this.tables[idx].isActive = this.dialogForm.isActive;
        }
        this.showToast("اطلاعات میز ذخیره شد", "success");
        this.closeTableDialog();
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.showToast("خطا در ذخیره‌سازی", "error");
      }
    },
    buildBulkUpdatePayload(table, overrides = {}) {
      return {
        tables: [
          {
            id: table.id,
            hall_id: table.hallId,
            number: String(table.number),
            capacity: table.capacity,
            x: table.x,
            y: table.y,
            shape: table.shape || "rectangle",
            status: overrides.status !== undefined ? overrides.status : table.status,
            is_active:
              overrides.isActive !== undefined ? overrides.isActive : table.isActive,
          },
        ],
      };
    },
    async onCardStatusChange(table, value) {
      const prev = table.status;
      table.status = value;
      try {
        if (this.$axios && this.$endpoint && table.id) {
          const payload = this.buildBulkUpdatePayload(table, { status: value });
          await this.$axios.put(
            this.$endpoint + "restaurant/tables/bulk-update",
            payload
          );
          this.showToast("وضعیت میز به‌روز شد", "success");
        }
      } catch (err) {
        table.status = prev;
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.showToast("خطا در به‌روزرسانی وضعیت", "error");
      }
    },
    async onCardActiveChange(table) {
      const next = !table.isActive;
      const prev = table.isActive;
      table.isActive = next;
      try {
        if (this.$axios && this.$endpoint && table.id) {
          const payload = this.buildBulkUpdatePayload(table, { is_active: next });
          await this.$axios.put(
            this.$endpoint + "restaurant/tables/bulk-update",
            payload
          );
          this.showToast(next ? "میز فعال شد" : "میز غیرفعال شد", "success");
        }
      } catch (err) {
        table.isActive = prev;
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.showToast("خطا در تغییر وضعیت فعال/غیرفعال", "error");
      }
    },
  },
};
</script>

<style scoped>
.restaurant-tables-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: #ffffff;
  border-radius: 5px;
  min-height: calc(100vh - 80px - 24px);
  height: 100%;
  box-sizing: border-box;
}

.restaurant-tables-page :deep(.ab-ui-btn) {
  border-radius: 8px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-header__title h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.page-header__title p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #777;
}

.summary-cards {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.summary-card {
  flex: 1;
  min-width: 100px;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-card__value {
  font-size: 20px;
  font-weight: 600;
}

.summary-card__label {
  font-size: 12px;
  opacity: 0.9;
}

.summary-card--inactive {
  background-color: #f5f5f5;
  color: #616161;
}

.summary-card--reserved {
  background-color: #fff3e0;
  color: #e65100;
}

.summary-card--busy {
  background-color: #ffebee;
  color: #c62828;
}

.summary-card--free {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.summary-card--total {
  background-color: #fff;
  color: #333;
  border: 1px solid #eee;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.view-toggle {
  display: flex;
  gap: 6px;
}

.view-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background-color: #fafafa;
  font-size: 12px;
  cursor: pointer;
}

.view-toggle__btn--active {
  background-color: #fff;
  border-color: var(--v-theme-primary, #1976d2);
  color: var(--v-theme-primary, #1976d2);
}

.hall-filter {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.hall-filter__btn {
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid #eee;
  background-color: #fff;
  font-size: 12px;
  cursor: pointer;
}

/* همان آبی دکمه فاکتور جدید — آبی primary */
.restaurant-tables-page .hall-filter__btn.hall-filter__btn--active {
  border-color: #1976d2 !important;
  background-color: #1976d2 !important;
  color: #fff !important;
}

.legend {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-item::before {
  content: "";
  width: 10px;
  height: 10px;
  border-radius: 3px;
  display: inline-block;
}

.legend-item--free::before {
  background-color: #4caf50;
}
.legend-item--busy::before {
  background-color: #f44336;
}
.legend-item--reserved::before {
  background-color: #ff9800;
}
.legend-item--inactive::before {
  background-color: #9e9e9e;
}

.content-card {
  flex: 1 1 0;
  min-height: 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.content-card__body {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.loading-wrap {
  padding: 32px;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.canvas-header {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #555;
}

.canvas {
  position: relative;
  flex: 1 1 0;
  min-height: 280px;
  border-radius: 12px;
  background-image: radial-gradient(#e0e0e0 1px, transparent 1px);
  background-size: 16px 16px;
  background-color: #fafafa;
  overflow: hidden;
}

.table-visual-wrap {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.table-visual-wrap--clickable {
  cursor: pointer;
}

.table-visual {
  min-width: 56px;
  min-height: 40px;
  padding: 6px 10px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border-radius: 10px;
  border: 2px solid #4caf50;
  background-color: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
}

.table-visual--disabled {
  opacity: 0.7;
  filter: grayscale(0.3);
}

.table-visual--circle {
  border-radius: 999px;
  width: 48px;
  height: 48px;
}

.table-visual--square {
  width: 52px;
  height: 52px;
}
.table-visual--rectangle {
  min-width: 72px;
  height: 44px;
}

.table-visual--status-free {
  border-color: #4caf50;
  background-color: #e8f5e9;
  color: #2e7d32;
}
.table-visual--status-busy {
  border-color: #f44336;
  background-color: #ffebee;
  color: #c62828;
}
.table-visual--status-reserved {
  border-color: #ff9800;
  background-color: #fff3e0;
  color: #ef6c00;
}
.table-visual--status-inactive {
  border-color: #9e9e9e;
  background-color: #f5f5f5;
  color: #616161;
}

.table-visual__number {
  font-weight: 600;
}
.table-visual__seats {
  font-size: 10px;
}
.table-visual__guests {
  font-size: 10px;
  opacity: 0.9;
}

.table-cards {
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
  align-content: start;
}

.table-card {
  position: relative;
  border-radius: 10px;
  border: 2px solid #4caf50;
  background-color: #fff;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.table-card--free {
  border-color: #4caf50;
}
.table-card--busy {
  border-color: #f44336;
}
.table-card--reserved {
  border-color: #ff9800;
}
.table-card--inactive {
  border-color: #9e9e9e;
}

.table-card__dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.table-card--free .table-card__dot {
  background-color: #4caf50;
}
.table-card--busy .table-card__dot {
  background-color: #f44336;
}
.table-card--reserved .table-card__dot {
  background-color: #ff9800;
}
.table-card--inactive .table-card__dot {
  background-color: #9e9e9e;
}

.table-card__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.table-card__title {
  font-size: 14px;
  font-weight: 600;
}

.table-card__hall {
  font-size: 11px;
  color: #777;
}

.table-card__capacity {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}

.table-card__status {
  display: flex;
  align-items: center;
  gap: 6px;
}

.table-card__status-select {
  flex: 1;
  max-width: 120px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  font-size: 12px;
  background: #fff;
  cursor: pointer;
}

.table-card__active-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.table-card__active-label {
  font-size: 12px;
  color: #555;
}

.table-card__active-toggle {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid #ddd;
  background: #e0e0e0;
  cursor: pointer;
  padding: 2px;
  position: relative;
  transition: background 0.2s;
}

.table-card__active-toggle--on {
  background: var(--v-theme-primary, #1976d2);
  border-color: var(--v-theme-primary, #1976d2);
}

.table-card__active-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: right 0.2s, left 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.table-card__active-toggle--on .table-card__active-dot {
  right: 2px;
  left: auto;
}

.table-card__active-toggle:not(.table-card__active-toggle--on) .table-card__active-dot {
  left: 2px;
  right: auto;
}

.table-card__status-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: #f0f0f0;
  color: #555;
}

.table-card__row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.table-card__label {
  color: #777;
}
.table-card__value {
  font-weight: 500;
}

/* دیالوگ اطلاعات میز */
.table-dialog__inner {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.table-dialog__title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}

.table-dialog__info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.table-dialog__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.table-dialog__label {
  color: #666;
  margin-left: 8px;
}

.table-dialog__value {
  font-weight: 500;
  color: #333;
}

.table-dialog__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.table-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.table-dialog__field--row {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.table-dialog__select {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  font-size: 13px;
  background: #fff;
  cursor: pointer;
}

.table-dialog__toggle {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid #ddd;
  background: #e0e0e0;
  cursor: pointer;
  padding: 2px;
  position: relative;
  transition: background 0.2s;
}

.table-dialog__toggle--on {
  background: var(--v-theme-primary, #1976d2);
  border-color: var(--v-theme-primary, #1976d2);
}

.table-dialog__toggle-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: right 0.2s, left 0.2s;
}

.table-dialog__toggle--on .table-dialog__toggle-dot {
  right: 2px;
  left: auto;
}

.table-dialog__toggle:not(.table-dialog__toggle--on) .table-dialog__toggle-dot {
  left: 2px;
  right: auto;
}

.table-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.table-dialog__btn {
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: #fff;
}

.table-dialog__btn--primary {
  background: var(--v-theme-primary, #1976d2);
  border-color: var(--v-theme-primary, #1976d2);
  color: #fff;
}

.table-dialog__btn--secondary:hover {
  background: #f5f5f5;
}
</style>
